import AsyncStorage from '@react-native-async-storage/async-storage';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { OmitKeyof } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

type CacheConfig = {
  staleTime?: number;
  gcTime?: number;
};

const DEFAULT_CACHING: CacheConfig = {
  staleTime: MINUTE,
  // Maximum `gcTime` is 24 days
  // https://tanstack.com/query/latest/docs/framework/react/plugins/persistQueryClient#how-it-works
  gcTime: DAY
};

const NO_CACHING: CacheConfig = {
  staleTime: 0,
  gcTime: 5 * MINUTE
};

// Caching preset for data defined via data-init.
// This data rarely changes and therefore has a long retention period.
const DATA_INIT_CACHING: CacheConfig = {
  // In development we disable caching so every mount refetches fresh data.
  staleTime: __DEV__ ? 0 : DAY,
  gcTime: 7 * DAY
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...DEFAULT_CACHING,
      // Future Todo: set retry based on status code
      retry: false,
      networkMode: 'online'
    }
  }
});

const persister = createAsyncStoragePersister({
  storage: AsyncStorage
});

type PersistOptions = OmitKeyof<PersistQueryClientOptions, 'queryClient'>;

export const persistOptions: PersistOptions = {
  persister,
  // It should be set as the same value or lower than all `gcTime` options.
  // https://tanstack.com/query/latest/docs/framework/react/plugins/persistQueryClient#how-it-works
  maxAge: 5 * MINUTE
};

export { DEFAULT_CACHING, DATA_INIT_CACHING, NO_CACHING };
